import { Field } from "components/dynamic/DynamicForm";
import DynamicFilter from "components/filter/DynamicFilter";
import MobileFilterDrawer from "components/filter/MobileFilterDrawer";
import HeaderBox from "components/navbar/HeaderBox";
import DynamicTable from "components/table/DynamicTable";
import Pagination from "components/table/Pagination";
import { useModal } from "hooks/useModal";
import { useScope } from "hooks/useScope";
import moment from "moment-jalaali";
import { useState } from "react";
import { FieldValues } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { BiSolidMessageSquareDetail, BiSolidReport } from "react-icons/bi";
import { useNavigate } from "react-router-dom";
import {
  useCreateAssessmentMutation,
  useGetAssessmentsQuery,
  useTransferAssessmentMutation,
} from "reduxFiles/api/assessmentApi";
import {
  useGetIndustriesQuery,
  useGetProvincesQuery,
  useLazyGetCompaniesQuery,
  useLazyGetUserOrganizationsQuery,
} from "reduxFiles/api/companyApi";
import { enumToOptions } from "services/helper";
import {
  Assessment,
  AssessmentStatus,
  GroupType,
  LifeCyclePosition,
} from "types/assessmentTypes";
import {
  ActivityType,
  Company,
  CompanyStage,
  CompanyType,
} from "types/companyTypes";
import { Column, TableAction } from "types/globalTypes";
import CreateAssessmentModal from "./CreateAssessmentModal";
import { RiFileTransferFill } from "react-icons/ri";
import { useLazyGetUsersQuery } from "reduxFiles/api/userApi";
import { useAppSelector } from "reduxFiles/hooks";
import { UserProfile } from "types/userTypes";

const AssessmentList = () => {
  // hooks
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const navigate = useNavigate();
  const scope = useScope();
  const user: UserProfile = useAppSelector((state) => state.auth.user);

  // states
  const modal = useModal();
  const transferModal = useModal();
  const [selectedAssessment, setSelectedAssessment] =
    useState<Assessment | null>(null);

  // filters
  const [filters, setFilters] = useState({});
  const [showFilters, setShowFilters] = useState(false);

  // pagination
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
  });

  // apis
  const { data, isFetching, refetch } = useGetAssessmentsQuery({
    scope,
    page: pagination.page,
    limit: pagination.limit,
    filters,
  });

  const [addApi, { isLoading: isAdding }] = useCreateAssessmentMutation();
  const [transferApi, { isLoading: isTransferring }] =
    useTransferAssessmentMutation();

  // provinces
  const { data: provinces } = useGetProvincesQuery();
  const { data: industries } = useGetIndustriesQuery();

  // companies
  const [triggerCompanies] = useLazyGetCompaniesQuery();
  const searchCompanies = async (text: string) => {
    try {
      const res = await triggerCompanies({
        page: 1,
        limit: 50,
        filters: { name: text },
      }).unwrap();

      return res.items.map((u) => ({
        label: u.name,
        value: u.id,
      }));
    } catch (err) {
      console.error(err);
      return [];
    }
  };

  // organizations
  const [triggerOrganizations] = useLazyGetUserOrganizationsQuery();
  const searchOrganizations = async (text: string) => {
    try {
      const res = await triggerOrganizations({
        page: 1,
        limit: 50,
        filters: { name: text },
      }).unwrap();

      return res.items.map((u) => ({
        label: u.name,
        value: u.id,
      }));
    } catch (err) {
      console.error(err);
      return [];
    }
  };

  // industrials
  const [triggerIndustrials] = useLazyGetUserOrganizationsQuery();
  const searchIndustrials = async (text: string) => {
    try {
      const res = await triggerIndustrials({
        page: 1,
        limit: 50,
        filters: { name: text, industrial: true },
      }).unwrap();

      return res.items.map((u) => ({
        label: u.name,
        value: u.id,
      }));
    } catch (err) {
      console.error(err);
      return [];
    }
  };

  // users
  const [triggerUsers] = useLazyGetUsersQuery();
  const searchUsers = async (text: string) => {
    try {
      const res = await triggerUsers({
        page: 1,
        limit: 50,
        filters: { search: text },
      }).unwrap();

      return res.items.map((u) => ({
        label: u.fullname + " " + u.mobile || "-",
        value: u.id,
      }));
    } catch (err) {
      console.error(err);
      return [];
    }
  };

  // form
  const fields: Field[] = [
    {
      name: "userId",
      label: t("licenses.userId"),
      type: "select",
      onSearch: searchUsers,
      filtered: scope === "admin",
    },
    {
      name: "companyId",
      label: t("assessment.companyId"),
      type: "select",
      onSearch: searchCompanies,
      formed: true,
      required: true,
      filtered: true,
    },
    {
      name: "nationalId",
      label: t("company.fields.nationalId"),
      type: "text",
      filtered: true,
    },
    {
      name: "organizationId",
      label: t("organization.name"),
      type: "select",
      onSearch: searchOrganizations,
      filtered: scope === "admin",
    },
    {
      name: "industrialId",
      label: t("organization.industrial"),
      type: "select",
      onSearch: searchIndustrials,
      filtered: scope === "admin",
    },
    {
      name: "licenseKey",
      label: t("assessment.licenseKey"),
      type: "text",
      formed: true,
      required: true,
    },
    {
      name: "status",
      label: t("assessment.status"),
      type: "select",
      options: enumToOptions(AssessmentStatus, t, "assessment.statuses"),
      filtered: true,
    },
    {
      name: "position",
      label: t("assessment.industryLifeCycle.title"),
      type: "select",
      options: enumToOptions(
        LifeCyclePosition,
        t,
        "assessment.industryLifeCycle.stages"
      ),
      filtered: true,
    },
    {
      name: "ohiFrom",
      label: t("assessment.report.ohiFrom"),
      type: "number",
      filtered: true,
    },
    {
      name: "ohiTo",
      label: t("assessment.report.ohiTo"),
      type: "number",
      filtered: true,
    },
    {
      name: "industrySectorId",
      label: t("company.fields.industrySectorId"),
      type: "select",
      options:
        scope === "organization" && user.organization?.industrials?.length > 0
          ? user.organization?.industrials?.map((opt) => ({
              label: opt.name,
              value: opt.id,
            }))
          : industries?.map((opt) => ({
              label: opt.name,
              value: opt.id,
            })),
      filtered: scope !== "user",
    },
    {
      name: "companyType",
      label: t("company.fields.companyType"),
      type: "select",
      options: enumToOptions(CompanyType, t, "company.companyTypes"),
      filtered: scope !== "user",
    },
    {
      name: "activityType",
      label: t("company.fields.activityType"),
      type: "select",

      options: enumToOptions(ActivityType, t, "company.activityTypes"),
      filtered: scope !== "user",
    },
    {
      name: "companyStage",
      label: t("company.fields.companyStage"),
      type: "select",
      options: enumToOptions(CompanyStage, t, "company.companyStages"),
      filtered: scope !== "user",
    },
    {
      name: "companySize",
      label: t("company.fields.companySize"),
      type: "select",
      options: enumToOptions(GroupType, t, "group.types"),
      filtered: true,
    },
    {
      name: "provinceId",
      label: t("company.fields.province"),
      type: "select",
      options:
        scope === "organization" && user.organization?.provinces?.length > 0
          ? user.organization?.provinces?.map((opt) => ({
              label: opt.name,
              value: opt.id,
            }))
          : provinces?.map((opt) => ({
              label: opt.name,
              value: opt.id,
            })),
      filtered: scope === "admin",
    },
    {
      name: "isKnowledgeBased",
      label: t("company.fields.isKnowledgeBased"),
      type: "select",
      options: [
        { label: t("common.yes"), value: "true" },
        { label: t("common.no"), value: "false" },
      ],
      filtered: scope !== "user",
    },
    {
      name: "isTechnologist",
      label: t("company.fields.isTechnologist"),
      type: "select",
      options: [
        { label: t("common.yes"), value: "true" },
        { label: t("common.no"), value: "false" },
      ],
      filtered: scope !== "user",
    },
    {
      name: "hasExport",
      label: t("company.fields.hasExport"),
      type: "select",
      options: [
        { label: t("common.yes"), value: "true" },
        { label: t("common.no"), value: "false" },
      ],
      filtered: scope !== "user",
    },
    {
      name: "startDate",
      label: t("table.startDate"),
      type: "date",
      filtered: true,
    },
    {
      name: "endDate",
      label: t("table.endDate"),
      type: "date",
      filtered: true,
    },
  ];

  const columns: Column<Assessment>[] = [
    {
      key: "createdAt",
      title: t("table.createdAt"),
      render: (row) =>
        moment(row.createdAt).format(
          lang === "fa" ? "jYYYY/jMM/jDD HH:mm" : "YYYY/MM/DD HH:mm"
        ),
    },
    {
      key: "company",
      title: t("assessment.companyId"),
      render: (row) => row.company?.name ?? "",
    },
    {
      key: "companyId",
      title: t("company.fields.nationalId"),
      render: (row) => row.company?.nationalId ?? "",
    },
    {
      key: "organization",
      title: t("assessment.organization"),
      render: (row) =>
        row.organization
          ? `${row.organization?.name} ${
              row.organization?.province?.name ?? ""
            }`
          : "-",
    },
    {
      key: "status",
      title: t("assessment.status"),
      render: (row) => t(`assessment.statuses.${row.status}`),
    },
  ];

  const actions: TableAction<Assessment>[] = [
    {
      label: t("assessment.assessmentReferal"),
      onClick: (row) => {
        setSelectedAssessment(row);
        transferModal.openModal();
      },
      color: "secondary",
      icon: <RiFileTransferFill />,
      hidden: (row) => scope !== "user" || row.status === "PENDING",
    },
    {
      label: t("assessment.assessmentDetail"),
      onClick: (row) => {
        navigate(
          `/${scope === "user" ? "company" : scope}/assessment/${row.id}/detail`
        );
      },
      color: "primary",
      icon: <BiSolidMessageSquareDetail />,
      hidden: scope === "organization",
    },
    {
      label: t("assessment.assessmentReport"),
      onClick: (row) => {
        navigate(
          `/${scope === "user" ? "company" : scope}/assessment/${row.id}/report`
        );
      },
      color: "success",
      icon: <BiSolidReport />,
      hidden: (row) => row.status === "PENDING",
    },
  ];

  // functions
  const handleFilterSubmit = (filterData: FieldValues) => {
    setFilters(filterData);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleLimitChange = (newLimit: number) => {
    setPagination({
      page: 1,
      limit: newLimit,
    });
  };

  const handleSubmitWithCompany = async (
    licenseKey: string,
    selectedCompany: Company,
    organizationId: number,
    handleClose: () => void
  ) => {
    if (!selectedCompany) return;

    try {
      await addApi({
        companyId: selectedCompany?.id,
        organizationId: organizationId,
        licenseKey: licenseKey,
      }).unwrap();
      modal.closeModal();
      handleClose();
      refetch();
    } catch (err) {
      console.error(err);
    }
  };

  const handleTransferAssessment = async (
    licenseKey: string,
    selectedCompany: Company,
    organizationId: number,
    handleClose: () => void
  ) => {
    if (!selectedAssessment || !selectedCompany) return;

    try {
      await transferApi({
        assessmentId: selectedAssessment.id,
        licenseKey: licenseKey,
        organizationId: organizationId,
      }).unwrap();
      transferModal.closeModal();
      setSelectedAssessment(null);
      handleClose();
      refetch();
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div>
      <div className="mt-5 h-full">
        <HeaderBox
          title={t("assessment.assessmentList")}
          createBtn={
            scope === "user"
              ? {
                  title: t("assessment.addAssessment"),
                  handler: () => {
                    modal.openModal();
                  },
                }
              : undefined
          }
          filterBtn={{
            title: t("buttons.filters"),
            handler: () => setShowFilters((prev) => !prev),
          }}
          refreshBtn={refetch}
        />

        <MobileFilterDrawer
          open={showFilters}
          onClose={() => setShowFilters((prev) => !prev)}
          onSubmit={handleFilterSubmit}
          fields={fields?.filter((f) => f.filtered)}
          loading={isFetching}
        />
        <DynamicFilter
          onSubmit={handleFilterSubmit}
          fields={fields?.filter((f) => f.filtered)}
          defaultValues={{}}
          loading={isFetching}
        />

        <DynamicTable<Assessment>
          columns={columns}
          data={data?.items ?? []}
          actions={actions}
          loading={isFetching}
          pagination={pagination}
        />
        <Pagination
          currentPage={pagination?.page || 1}
          totalPages={data?.pageCount || 1}
          onPageChange={(newPage) => {
            setPagination((prev) => ({ ...prev, page: newPage }));
          }}
          totalItems={data?.total || 0}
          currentLimit={pagination.limit}
          onLimitChange={handleLimitChange}
        />

        {scope === "user" && (
          <CreateAssessmentModal
            isOpen={modal.isOpen}
            onClose={() => {
              modal.closeModal();
            }}
            onSubmit={handleSubmitWithCompany}
            loading={isAdding}
            title={t("assessment.addAssessment")}
            showCompanySelect={true}
          />
        )}

        {scope === "user" && (
          <CreateAssessmentModal
            isOpen={transferModal.isOpen}
            onClose={() => {
              transferModal.closeModal();
              setSelectedAssessment(null);
            }}
            onSubmit={handleTransferAssessment}
            loading={isTransferring}
            title={t("assessment.transferAssessment")}
            assessment={selectedAssessment}
            isTransferMode={true}
            company={selectedAssessment?.company ?? undefined}
          />
        )}
      </div>
    </div>
  );
};

export default AssessmentList;
